import { createRouter, RouterOptions, createWebHashHistory, createWebHistory } from "vue-router"
import { forgetPostLoginPath, markPostLoginPathDelivered, peekOriginalUriFromOkta, peekPostLoginPath, readPostLoginPath, recentlyDeliveredPath } from "../auth/postLoginRedirect"
import login from "./login.vue"
import frame from "./frame.vue"

import contactList from "./contactList.vue"
import projectList from "./projectList.vue"
import subProjectList from "./subProjectList.vue"
import assessmentList from "./assessmentList.vue"
import systemConfigurationThemeTemplate from "./systemConfiguration/systemConfigurationThemeTemplate.vue"
import dog from "./dog.vue"
import userList from "./userList.vue"
import userEdit from "./userEdit.vue"
import shinyPOC from "./shinyPOC.vue"
import newsList from "./newsList.vue"
import landingPage from "./landingPage.vue"
import assessment from "./assessment.vue"
import { routeGuards } from "../auth/routeGuards"
import AuthCallback from "./AuthCallback.vue"
import bookmarkedAssessmentList from "./bookmarkedAssessmentList.vue"

// Route meta convention used by the support-request feature:
//   meta.supportLocation : human-friendly label shown as "Location" in the support form.
//   meta.captureScope    : identifier — only routes with "assessment" scope contribute
//                          tab/subTab info captured from the assessment context store.

const routerOptions: RouterOptions = {
	history: createWebHistory("/"),
	//history: createWebHashHistory(),
	routes: [
		{ path: "/", name: "rootLogin", component: login },
		{ path: "/login", name: "login", component: login },
		{ path: "/dog", name: "dog", component: dog },
		{ path: "/login/callback", name: "callback", component: AuthCallback },
		{ path: "/logout/callback", name: "logoutCallback", component: login },
		//{ path: "/logout", name: "logout", component: login },
		{
			path: "/app",
			name: "frame",
			component: frame,
			beforeEnter: [routeGuards.requiresOktaLoginWithTheme],

			children: [
				{
					path: "",
					name: "landing",
					component: landingPage,
					meta: { supportLocation: "Home", captureScope: "home" },
				},
				{
					path: "helpAdmin",
					name: "helpAdmin",
					component: () => import("./helpAdminList.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Help administration", captureScope: "admin" },
				},

				{
					path: "systemConfiguration",
					name: "systemConfiguration",
					component: () => import("./systemConfiguration.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "System configuration", captureScope: "admin" },
				},
				{
					path: "dropdownAdministration",
					name: "dropdownAdministration",
					component: () => import("./systemConfiguration/dropdownAdministration.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Dropdown administration", captureScope: "admin" },
				},
				{
					path: "materialAdministration",
					name: "materialAdministration",
					component: () => import("./systemConfiguration/materialAdministration.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Material administration", captureScope: "admin" },
				},
				{
					path: "transportAdministration",
					name: "transportAdministration",
					component: () => import("./transportAdministration.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Transport administration", captureScope: "admin" },
				},
				{
					path: "unitOperationAdministration",
					name: "unitOperationAdministration",
					component: () => import("./systemConfiguration/unitOperationAdministration.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Unit operation administration", captureScope: "admin" },
				},
				{
					path: "energyAdmin",
					name: "energyAdmin",
					component: () => import("./energyAdmin.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Energy administration", captureScope: "admin" },
				},
				{
					path: "shinyPOC",
					name: "shinyPOC",
					component: shinyPOC,
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Shiny POC", captureScope: "other" },
				},
				{
					path: "contactList",
					name: "contactList",
					component: contactList,
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Contacts list", captureScope: "list" },
				},
				{
					path: "userList",
					name: "userList",
					component: userList,
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Users list", captureScope: "admin" },
				},
				{
					path: "userType",
					name: "userType",
					component: () => import("./userTypeList.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "User types administration", captureScope: "admin" },
				},
				{
					path: "newsList",
					name: "newsList",
					component: newsList,
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "News", captureScope: "list" },
				},
				{
					path: "landingPage",
					name: "landingPage",
					component: landingPage,
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Home", captureScope: "home" },
				},
				{
					path: "userProfile",
					name: "userProfile",
					component: () => import("./userProfile.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "User profile", captureScope: "other" },
				},
				{
					path: "projectList",
					name: "projectList",
					component: projectList,
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Projects list", captureScope: "list" },
				},
				{
					path: "subProjectList", ///:projectId
					name: "subProjectList",
					component: subProjectList,
					props: true,
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Sub-projects list", captureScope: "list" },
				},
				{
					path: "assessmentList", ///:subprojectId
					name: "assessmentList",
					component: assessmentList,
					props: true,
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Assessments list", captureScope: "list" },
				},
				{
					path: "bookmarkedAssessmentList", ///:subprojectId
					name: "bookmarkedAssessmentList",
					component: bookmarkedAssessmentList,
					props: true,
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Bookmarked assessments", captureScope: "list" },
				},
				{
					path: "assessment", ///:subprojectId
					name: "assessment",
					component: assessment,
					props: true,
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Assessment editor", captureScope: "assessment" },
				},
				{
					path: "systemConfigurationThemeTemplate",
					name: "systemConfigurationThemeTemplate",
					component: systemConfigurationThemeTemplate,
					props: true,
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Theme configuration", captureScope: "admin" },
				},
				{
					path: "mgmSingleUtilitiesCheck",
					name: "mgmSingleUtilitiesCheck",
					component: () => import("./systemConfiguration/mgmSingleUtilitiesCheck.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Single utilities check", captureScope: "admin" },
				},
				{
					path: "relatableUnitAdmin",
					name: "relatableUnitAdmin",
					component: () => import("./relatableUnitAdminList.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Relatable unit administration", captureScope: "admin" },
				},
				{
					path: "relatableUnitAdminEdit/:id?",
					name: "relatableUnitAdminEdit",
					component: () => import("./relatableUnitAdminEdit.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Relatable unit editor", captureScope: "admin" },
				},
				{
					path: "materialData",
					name: "materialData",
					component: () => import("./materialDataBrowse.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Material data browse", captureScope: "list" },
				},
				{
					path: "knowledge",
					name: "knowledge-base",
					component: () => import("./knowledge/KnowledgeList.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Knowledge base", captureScope: "knowledge" },
				},
				{
					path: "knowledge/:id",
					name: "knowledge-article",
					component: () => import("./knowledge/KnowledgeArticleView.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Knowledge article", captureScope: "knowledge" },
				},
				{
					path: "knowledge/:id/edit",
					name: "knowledge-article-edit",
					component: () => import("./knowledge/KnowledgeArticleDetail.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Knowledge article editor", captureScope: "knowledge" },
				},
				{
					path: "support",
					name: "supportRequestsList",
					component: () => import("./supportRequestsList.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Support requests list", captureScope: "admin" },
				},
				{
					path: "support/:id",
					name: "supportRequestEdit",
					component: () => import("./supportRequestEdit.vue"),
					beforeEnter: [routeGuards.requiresOktaLogin],
					meta: { supportLocation: "Support request editor", captureScope: "admin" },
				},
			],
		},
	],
}

const router = createRouter(routerOptions)

// Reset stale assessment-only context when leaving an assessment route, so the
// next support request doesn't carry over the previous tab/subTab.
router.beforeEach((to, from, next) => {
	const leavingAssessment = from.meta?.captureScope === "assessment" && to.meta?.captureScope !== "assessment"
	if (leavingAssessment) {
		// Lazy-import the store to avoid a hard dependency at module load time.
		import("@/store/currentContextStore").then(({ useCurrentContextStore }) => {
			useCurrentContextStore().clear()
		})
	}

	// Signing in ends on the landing page, pushed by whichever of the callback screen and the
	// login screen finishes first. When a destination was written down before the login — a
	// support request opened from an email link — that is where the reader was going, so the
	// landing page hands over to it. Doing this here rather than in those screens means it holds
	// however many of them fire, and the destination is used once: reading it clears it.
	if (to.path === "/app" || to.path === "/app/") {
		const remembered = peekPostLoginPath() ?? recentlyDeliveredPath() ?? peekOriginalUriFromOkta()
		if (remembered && remembered !== to.fullPath) {
			next(remembered)
			return
		}
	}

	next()
})

// Dropped on arrival, and only here.
//
// It used to be dropped by the route guard, the moment it let the navigation through. That is not
// arrival: the navigation is still in flight, and the login screen's own push to /app — which runs
// seconds after it was mounted, while the router is still on / — could land afterwards and take the
// reader to the landing page with nothing left to send them back. This runs when a navigation has
// actually finished, so the destination survives exactly as long as it is needed.
router.afterEach((to) => {
	// Arriving without a session is not arriving. The navigation completes, and only then does the
	// sign-in start and take the browser to the identity provider — so a destination dropped here
	// is gone by the time the reader comes back, which is exactly what sent them to the landing
	// page. Read straight from storage: SessionInfo.currentUser() starts that redirect by itself.
	if (!localStorage.getItem("PersonSessionData")) return

	const remembered = readPostLoginPath()
	if (!remembered) return

	// Arrived where the reader was going, or somewhere else inside the application that they
	// asked for themselves: either way there is nothing left to restore.
	if (to.fullPath === remembered) {
		markPostLoginPathDelivered()
	} else if (to.path.startsWith("/app/") && to.path !== "/app/") {
		// Somewhere else inside the application, chosen by the reader: the destination has been
		// superseded and there is nothing left to protect.
		forgetPostLoginPath()
	}
})

export default router
